import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Lenis from "lenis";
import { ArrowUpRight, Instagram, Mail, Package, Sparkles, ShieldCheck, Flame } from "lucide-react";

const BEARPACK_URL = "https://bearpack.in";

const IMG = {
  heroFabric:
    "https://static.prod-images.emergentagent.com/jobs/846b66bc-4848-4e18-befe-d9ad1c2be409/images/bd5ca05cd90d9224a8931dabad8fa65223649e006cfcf0c52571773941a15d3b.png",
  glass:
    "https://static.prod-images.emergentagent.com/jobs/846b66bc-4848-4e18-befe-d9ad1c2be409/images/057275ef6927a2ea797502bc10926e0069548f3a067d29a6869ac8b5ae023174.png",
  model1: "https://images.pexels.com/photos/30047308/pexels-photo-30047308.jpeg",
  model2: "https://images.unsplash.com/photo-1768084356884-22bb77e76931",
  model3: "https://images.pexels.com/photos/13911532/pexels-photo-13911532.jpeg",
  hoodie: "https://images.unsplash.com/photo-1508216310976-c518daae0cdc",
};

/* ───────────────────────────── Header ───────────────────────────── */
const Header = () => (
  <motion.header
    initial={{ y: -40, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="fixed top-0 inset-x-0 z-50"
    data-testid="global-header"
  >
    <div className="mx-auto max-w-7xl px-6 py-4">
      <div className="glass rounded-full px-5 sm:px-7 py-3 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="header-logo">
          <span className="font-display font-black tracking-tighter text-lg sm:text-xl text-white">
            BEAR<span className="text-white/60">BICEPS</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-7 text-sm text-white/70 font-body">
          <a href="#story" className="hover:text-white transition-colors" data-testid="nav-story">Story</a>
          <a href="#pillars" className="hover:text-white transition-colors" data-testid="nav-pillars">Pillars</a>
          <a href="#drops" className="hover:text-white transition-colors" data-testid="nav-drops">Drops</a>
          <a href="#pack" className="hover:text-white transition-colors" data-testid="nav-pack">The Pack</a>
        </nav>
        <a
          href={BEARPACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-1.5 rounded-full bg-white text-black px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold font-display tracking-tight hover:bg-white/90 transition-all"
          data-testid="header-shop-cta"
        >
          Shop BEARPACK
          <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </div>
  </motion.header>
);

/* ───────────────────────────── Hero ───────────────────────────── */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden hero-bg grain"
      data-testid="hero-section"
    >
      {/* Fabric backdrop */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -z-0"
      >
        <img
          src={IMG.heroFabric}
          alt="BEARPACK premium heavyweight cotton fabric texture"
          className="w-full h-full object-cover opacity-[0.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
      </motion.div>

      {/* Hero copy */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 flex flex-col items-center justify-center min-h-[100svh] px-6 text-center"
      >
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-[11px] sm:text-xs tracking-[0.25em] text-white/70 font-display uppercase mb-8"
          data-testid="hero-eyebrow"
        >
          <span className="size-1.5 rounded-full bg-white animate-pulse" />
          BEARPACK by BEARBICEPS
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black tracking-tighter text-white text-[16vw] sm:text-[12vw] md:text-[9vw] leading-[0.9] drop-shadow-[0_4px_40px_rgba(255,255,255,0.08)]"
          data-testid="hero-title"
        >
          BEARPACK
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 1 }}
          className="mt-14 max-w-2xl text-balance font-display text-base sm:text-lg md:text-2xl text-white/80 font-light tracking-tight"
          data-testid="hero-subtitle"
        >
          Built for the culture. Oversized fits. Premium quality. Limited drops.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-3"
        >
          <a
            href={BEARPACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3.5 font-display font-semibold text-sm tracking-tight hover:bg-white/90 transition-all glow-soft"
            data-testid="hero-primary-cta"
          >
            Enter bearpack.in
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#story"
            className="inline-flex items-center gap-2 rounded-full glass px-7 py-3.5 font-display font-semibold text-sm tracking-tight text-white/90 hover:bg-white/10 transition-all"
            data-testid="hero-secondary-cta"
          >
            The Story
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-10 inset-x-0 flex justify-center"
        >
          <div className="flex flex-col items-center gap-2 text-[10px] tracking-[0.3em] text-white/40 font-display">
            <span>SCROLL</span>
            <span className="h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ───────────────────────────── Marquee ───────────────────────────── */
const Marquee = () => {
  const items = [
    "BEARPACK BY BEARBICEPS",
    "OVERSIZED FITS",
    "HEAVYWEIGHT COTTON",
    "LIMITED DROPS",
    "WELCOME TO THE PACK",
    "SHOP AT BEARPACK.IN",
  ];
  const row = [...items, ...items];
  return (
    <section className="relative border-y border-white/10 bg-black py-6 overflow-hidden" data-testid="marquee">
      <div className="flex w-[200%] animate-marquee">
        {row.map((t, i) => (
          <div key={i} className="flex items-center px-8 whitespace-nowrap">
            <span className="font-display font-black tracking-tighter text-2xl sm:text-3xl text-white/85">
              {t}
            </span>
            <span className="ml-8 size-1.5 rounded-full bg-white/30" />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────────── Brand Story ─────────────────────────── */
const STORY_LINES = [
  "A lot of people were confused…",
  "Is it BEARBICEPS or BEARPACK?",
  "So here's the clarity.",
  "BEARBICEPS was always the company — the backbone, the system behind everything.",
  "But what you wear, what you see, what you represent…",
  ["That's ", <span key="bp" className="text-white font-black">BEARPACK</span>, "."],
  "A streetwear brand built on oversized fits, premium quality, and limited drops.",
  "From now on, everything moves under one identity.",
  "BEARPACK.",
  "Same vision. Same team.",
  "Just sharper, louder, and built for the culture.",
  "This isn't a rebrand. This is focus.",
  "Welcome to the pack.",
];

const StoryLine = ({ children, big }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px -15% 0px" });
  return (
    <motion.p
      ref={ref}
      initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
      animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className={`font-display tracking-tight text-balance ${
        big
          ? "text-5xl sm:text-7xl md:text-8xl font-black text-white"
          : "text-2xl sm:text-3xl md:text-4xl font-light text-white/85"
      } leading-[1.1]`}
    >
      {children}
    </motion.p>
  );
};

const BrandStory = () => (
  <section
    id="story"
    className="relative bg-black py-32 sm:py-48 px-6"
    data-testid="brand-story-section"
  >
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mb-16 text-center"
      >
        <span className="inline-block text-[11px] tracking-[0.3em] text-white/40 font-display uppercase">
          Chapter 01 — The Clarity
        </span>
      </motion.div>

      <div className="flex flex-col gap-14 sm:gap-20 text-center">
        {STORY_LINES.map((line, i) => (
          <StoryLine key={i} big={line === "BEARPACK."}>
            {line}
          </StoryLine>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mt-24 flex justify-center"
      >
        <a
          href={BEARPACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3.5 font-display font-semibold text-sm tracking-tight hover:bg-white/90 transition-all"
          data-testid="story-cta"
        >
          Step into bearpack.in
          <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ───────────────────────── Brand Pillars ───────────────────────── */
const PILLARS = [
  {
    icon: Sparkles,
    title: "Oversized Fits",
    desc: "Engineered silhouettes that drape with intention. Cut for movement, designed for presence.",
  },
  {
    icon: ShieldCheck,
    title: "Premium Quality",
    desc: "Heavyweight cottons. Reinforced stitching. Built to outlast trends, not chase them.",
  },
  {
    icon: Flame,
    title: "Limited Drops",
    desc: "Numbered runs. No restocks. Once it's gone, it lives only on the streets that wore it.",
  },
];

const PillarCard = ({ icon: Icon, title, desc, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="glass rounded-3xl p-8 sm:p-10 relative overflow-hidden group hover:bg-white/5 transition-colors"
      data-testid={`pillar-card-${index}`}
    >
      <div className="absolute -top-20 -right-20 size-56 rounded-full bg-white/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
      <Icon className="text-white/80 mb-6" size={28} strokeWidth={1.4} />
      <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-white mb-3">
        {title}
      </h3>
      <p className="text-white/60 font-body text-sm sm:text-base leading-relaxed">
        {desc}
      </p>
    </motion.div>
  );
};

const BrandPillars = () => (
  <section id="pillars" className="relative bg-black py-28 px-6 overflow-hidden" data-testid="brand-pillars">
    <div className="absolute inset-0 -z-0 opacity-50">
      <img src={IMG.glass} alt="" className="w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-black/70" />
    </div>
    <div className="relative max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="mb-16 max-w-3xl"
      >
        <span className="block text-[11px] tracking-[0.3em] text-white/40 font-display uppercase mb-4">
          What we stand on
        </span>
        <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95] text-balance">
          Three pillars.<br />
          <span className="text-white/55">One pack.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PILLARS.map((p, i) => (
          <PillarCard key={p.title} {...p} index={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ───────────────────────── Featured Drops ───────────────────────── */
const DROPS = [
  {
    src: IMG.model1,
    name: "Vault 01 — Concrete Pack",
    tag: "OVERSIZED HOODIE",
    alt: "BEARPACK oversized streetwear hoodie photographed against a concrete wall",
  },
  {
    src: IMG.model2,
    name: "Vault 02 — Night Vest",
    tag: "HEAVYWEIGHT VEST",
    alt: "BEARPACK premium streetwear vest in dark aesthetic lighting",
  },
  {
    src: IMG.model3,
    name: "Vault 03 — Studio Cut",
    tag: "LIMITED DROP",
    alt: "BEARPACK limited drop streetwear under dramatic studio lighting",
  },
];

const DropCard = ({ drop, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  return (
    <motion.a
      ref={ref}
      href={BEARPACK_URL}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group relative block overflow-hidden rounded-3xl bg-zinc-950 aspect-[4/5]"
      data-testid={`drop-card-${index}`}
    >
      <img
        src={drop.src}
        alt={drop.alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-between">
        <span className="self-start glass-strong rounded-full px-3 py-1 text-[10px] font-display tracking-[0.25em] text-white/80">
          {drop.tag}
        </span>
        <div className="flex items-end justify-between gap-4">
          <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-white">
            {drop.name}
          </h3>
          <span className="glass rounded-full p-3 group-hover:bg-white group-hover:text-black transition-colors">
            <ArrowUpRight size={18} />
          </span>
        </div>
      </div>
    </motion.a>
  );
};

const FeaturedDrops = () => (
  <section id="drops" className="relative bg-black py-28 px-6" data-testid="featured-drops">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6"
      >
        <div>
          <span className="block text-[11px] tracking-[0.3em] text-white/40 font-display uppercase mb-4">
            The Vaults
          </span>
          <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95] text-balance">
            Drops that <span className="text-white/55">don't wait.</span>
          </h2>
        </div>
        <a
          href={BEARPACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="self-start sm:self-end inline-flex items-center gap-2 rounded-full glass px-5 py-3 text-sm font-display font-semibold text-white/90 hover:bg-white/10 transition-colors"
          data-testid="drops-shop-all"
        >
          Shop all at bearpack.in
          <ArrowUpRight size={16} />
        </a>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {DROPS.map((d, i) => (
          <DropCard key={d.name} drop={d} index={i} />
        ))}
      </div>

      {/* Big feature image */}
      <motion.a
        href={BEARPACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="group relative block mt-5 overflow-hidden rounded-3xl aspect-[16/9] sm:aspect-[21/9]"
        data-testid="drops-feature-banner"
      >
        <img
          src={IMG.hoodie}
          alt="BEARPACK oversized black hoodie — flagship streetwear silhouette"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-8 sm:px-16 max-w-2xl">
          <span className="text-[11px] tracking-[0.3em] text-white/50 font-display uppercase mb-3">
            Flagship Silhouette
          </span>
          <h3 className="font-display font-black tracking-tighter text-4xl sm:text-6xl text-white leading-[0.95]">
            The Oversized.<br />Redefined.
          </h3>
          <p className="mt-4 max-w-md text-white/70 font-body">
            Heavyweight 480 GSM cotton. Boxy cut. Dropped shoulder. Made for the pack.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-white text-black px-6 py-3 font-display font-semibold text-sm group-hover:bg-white/90 transition-colors">
            Explore on bearpack.in
            <ArrowUpRight size={16} />
          </span>
        </div>
      </motion.a>
    </div>
  </section>
);

/* ───────────────────────── Join the Pack ───────────────────────── */
const JoinPack = () => (
  <section id="pack" className="relative bg-black py-32 sm:py-48 px-6 overflow-hidden" data-testid="join-pack-cta">
    <div className="absolute inset-0 -z-0">
      <img src={IMG.glass} alt="" className="w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-black/70" />
    </div>
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1 }}
      className="relative max-w-5xl mx-auto text-center"
    >
      <Package className="mx-auto text-white/80 mb-8" size={36} strokeWidth={1.3} />
      <h2 className="font-display font-black tracking-tighter text-6xl sm:text-7xl md:text-9xl leading-[0.9] text-balance">
        Welcome to <span className="text-white/60">the pack.</span>
      </h2>
      <p className="mt-8 max-w-xl mx-auto text-white/70 font-body text-base sm:text-lg">
        Early access to drops. Member-only releases. No noise. Only signal.
      </p>
      <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={BEARPACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-white text-black px-8 py-4 font-display font-semibold text-sm tracking-tight hover:bg-white/90 transition-all glow-soft"
          data-testid="pack-primary-cta"
        >
          Enter bearpack.in
          <ArrowUpRight size={16} />
        </a>
        <a
          href="https://instagram.com/bearpack.official"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full glass px-8 py-4 font-display font-semibold text-sm tracking-tight text-white/90 hover:bg-white/10 transition-all"
          data-testid="pack-secondary-cta"
        >
          <Instagram size={16} /> Follow @bearpack.official
        </a>
      </div>
    </motion.div>
  </section>
);

/* ───────────────────────────── Footer ───────────────────────────── */
const Footer = () => (
  <footer className="relative bg-black border-t border-white/10 px-6 pt-20 pb-10" data-testid="global-footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
        <div className="col-span-2">
          <h3 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-white mb-4">
            BEARBICEPS
          </h3>
          <p className="text-white/55 font-body text-sm max-w-md leading-relaxed">
            BEARBICEPS is the parent company behind <strong className="text-white/85">BEARPACK</strong> — a premium streetwear brand built on oversized fits, heavyweight cottons, and limited drops. One identity. One pack. Shop the latest at{" "}
            <a href={BEARPACK_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-white">bearpack.in</a>.
          </p>
        </div>

        <div>
          <h4 className="font-display font-semibold text-white/90 text-sm uppercase tracking-[0.2em] mb-4">Shop</h4>
          <ul className="space-y-3 text-white/55 font-body text-sm">
            <li><a href={BEARPACK_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-shop-main">bearpack.in</a></li>
            <li><a href={`${BEARPACK_URL}/collections`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Collections</a></li>
            <li><a href={`${BEARPACK_URL}/drops`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Drops</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-white/90 text-sm uppercase tracking-[0.2em] mb-4">Connect</h4>
          <ul className="space-y-3 text-white/55 font-body text-sm">
            <li>
              <a href="https://instagram.com/bearpack.official" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-ig-bearpack">
                <Instagram size={14} /> @bearpack.official
              </a>
            </li>
            <li>
              <a href="mailto:contact@bearpack.in" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-mail">
                <Mail size={14} /> contact@bearpack.in
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* SEO rich strap line */}
      <p className="text-[11px] text-white/35 font-body leading-relaxed border-t border-white/10 pt-8">
        BEARPACK by BEARBICEPS — premium Indian streetwear. Searching for{" "}
        <span className="text-white/60">bearbiceps</span>,{" "}
        <span className="text-white/60">bearpack</span>,{" "}
        <span className="text-white/60">bearpack.in</span>,{" "}
        <span className="text-white/60">bearbiceps streetwear</span>, or{" "}
        <span className="text-white/60">bearpack by bearbiceps</span>? You're in the right place. Visit <a href={BEARPACK_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-white">bearpack.in</a> for the latest oversized fits, heavyweight cotton drops, and limited streetwear releases.
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-[11px] tracking-[0.2em] uppercase text-white/40 font-display">
        <span>© {new Date().getFullYear()} BEARBICEPS · All rights reserved</span>
        <span>Same vision. Same team. One pack.</span>
      </div>
    </div>
  </footer>
);

/* ───────────────────────────── App ───────────────────────────── */
export default function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    // Smooth scroll for in-page anchor clicks
    const onClick = (e) => {
      const a = e.target.closest("a[href^='#']");
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -20 });
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      document.removeEventListener("click", onClick);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-black text-white" data-testid="app-root">
      {/* Visually hidden SEO strap mentioning BEARPACK by BEARBICEPS for crawlers */}
      <p className="sr-only">BEARPACK by BEARBICEPS — Premium Streetwear, Oversized Fits, Limited Drops at bearpack.in</p>
      <Header />
      <main>
        <Hero />
        <Marquee />
        <BrandStory />
        <BrandPillars />
        <FeaturedDrops />
        <JoinPack />
      </main>
      <Footer />
    </div>
  );
}
